import { useCallback, useEffect, useRef, useState } from "react";

import { FormProvider, useFormContext } from "react-hook-form";

import Box from "@/components/Box";
import Divider from "@/components/Divider";
import { InternalTextLink } from "@/components/Links";
import { Spinner } from "@/components/Spinner";
import { FadeTransition } from "@/components/animations/FadeTransition";
import SavePatientForm from "@/components/organisations/checkout/forms/SavePatientForm";
import { CMP_PERMISSION_ADMIN } from "@/components/organisations/constants";
import AddOnDetail from "@/components/organisations/shared/ui/AddOnDetail";
import { useOrganisationContext } from "@/contexts/organisations/OrganisationContext";
import RefetchEvent from "@/events/refetch";
import {
  ADD_ON_TO_STOCK_MUTATION,
  CREATE_STOCK_PAYMENT_INVOICE_MUTATION,
  REMOVE_ADD_ON_FROM_STOCK_MUTATION
} from "@/graphql/organisations/mutations";
import { ORGANISATION_COMMISSIONS_LIST_QUERY } from "@/graphql/organisations/queries/commission";
import {
  ORGANISATION_ORDER_BALANCE_QUERY,
  ORGANISATION_ORDERS_LIST_QUERY
} from "@/graphql/organisations/queries/orders";
import { ORGANISATION_LIST_QUERY } from "@/graphql/organisations/queries/organisation";
import {
  ORGANISATION_STOCK_BASKET_QUERY,
  ORGANISATION_STOCK_LIST_QUERY
} from "@/graphql/organisations/queries/stock";
import {
  APPLY_DISCOUNT_TO_STOCK_MUTATION,
  REMOVE_DISCOUNT_FROM_STOCK_MUTATION
} from "@/graphql/stock";
import useDjangoGraphqlForm from "@/hooks/form/useDjangoGraphqlForm";
import { useDidUpdate } from "@/hooks/useDidUpdate";
import { DYNAMIC_CMP_PERMISSION_QUERY } from "@/hooks/useDynamicCmpPermission";
import Center from "@/tpo/Center";
import ChevronComponent from "@/tpo/Chevron";
import ControlledFormField from "@/tpo/ControlledFormField";
import Currency from "@/tpo/Currency";
import FloatingLabelInput from "@/tpo/FloatingLabelInput";
import FormControl from "@/tpo/FormControl";
import { Error } from "@/tpo/InputWrapper";
import SelectPatientField from "@/tpo/SelectPatient";
import Spacer from "@/tpo/Spacer";
import Stack from "@/tpo/Stack";
import ButtonV2 from "@/v2/Buttons";
import { useMutation } from "@apollo/client";

import {
  getMissingFieldsForPatient,
  getSavePatientEditableFields
} from "../../shared/PatientDetails";
import PatientDetailsForRegisteringStock from "../PatientDetailsForRegisteringStock";

function FormSubmitButton({ loading, requiresBarcode }) {
  const { watch } = useFormContext();

  const [patientId, barcode] = watch(["patient", "barcode"]);
  const { organisation } = useOrganisationContext();
  const patient = organisation.patients.find(patient => patient.id === patientId);

  let canSubmitBasedOnPatient = false;
  const editableFields = patient ? getMissingFieldsForPatient(patient) : [];

  if (
    editableFields.length === 0 ||
    (editableFields.length === 1 && ["email", "guardianEmail"].includes(editableFields[0]))
  ) {
    // if the org is paying then let them proceed with checkout
    // unless something other than email is editable
    // in other words the only thing wrong is the email is a proxy
    canSubmitBasedOnPatient = true;
  }

  return (
    <ButtonV2
      color="green"
      rightIcon={loading ? null : <ChevronComponent />}
      disabled={!patient || !canSubmitBasedOnPatient || (requiresBarcode && !barcode) || loading}
      size={["sm", "sm", "md"]}
    >
      register & pay
    </ButtonV2>
  );
}

export default function RegisterStockPanel({
  stock,
  stockBasket,
  onComplete,
  showCreatePatientForm,
  setShowCreatePatientForm,
  showEditPatientForm,
  setShowEditPatientForm
}) {
  const hasBarcode = stock.omnosBarcodeId;
  const isCyrexStockProduct = stock.testProduct?.isCyrex;

  const { organisation, permissions } = useOrganisationContext();

  const [discountErrors, setDiscountErrors] = useState([]);
  const initialisedDiscountInput = useRef(false);
  const [discountInputValue, setDiscountInputValue] = useState("");

  useEffect(() => {
    if (!initialisedDiscountInput.current) {
      if (stockBasket?.appliedTradeDiscount?.code) {
        initialisedDiscountInput.current = true;
        setDiscountInputValue(stockBasket.appliedTradeDiscount.code);
      }
    } else {
      setDiscountInputValue(stockBasket?.appliedTradeDiscount?.code || "");
    }
  }, [stockBasket?.appliedTradeDiscount]);

  const [applyDiscountToStockMutation] = useMutation(APPLY_DISCOUNT_TO_STOCK_MUTATION);
  const [removeDiscountFromStockMutation] = useMutation(REMOVE_DISCOUNT_FROM_STOCK_MUTATION);

  const applyDiscountToStock = useCallback(
    discountCode => {
      applyDiscountToStockMutation({
        variables: {
          input: {
            organisation: organisation?.id,
            stock: stock?.id,
            discountCode,
            id: stockBasket?.id
          }
        }
      }).then(resp => {
        if (resp.data.applyDiscountToStockMutation.errors.length) {
          setDiscountErrors(
            resp.data.applyDiscountToStockMutation.errors.map(({ messages }) => messages[0])
          );
        } else {
          setDiscountErrors([]);
        }
      });
    },
    [applyDiscountToStockMutation, stock, organisation, stockBasket]
  );

  const removeDiscountFromStock = useCallback(() => {
    setDiscountErrors([]);
    removeDiscountFromStockMutation({
      variables: {
        input: {
          organisation: organisation?.id,
          stock: stock?.id,
          id: stockBasket?.id
        }
      }
    });
  }, [removeDiscountFromStockMutation, stock, organisation, stockBasket]);

  const formApi = useDjangoGraphqlForm({
    mutation: CREATE_STOCK_PAYMENT_INVOICE_MUTATION,
    mutationName: "createStockPaymentInvoiceMutation",
    mutationOptions: {
      refetchQueries: [
        {
          query: ORGANISATION_STOCK_LIST_QUERY,
          variables: {
            organisation: parseInt(organisation?.id),
            page: 1,
            orderBy: "-created"
          }
        },
        {
          query: ORGANISATION_ORDERS_LIST_QUERY,
          variables: {
            organisation: parseInt(organisation?.id),
            page: 1,
            orderBy: "-checkout_date"
          }
        }
      ]
    },
    handleSuccess: ({ data }) => {
      RefetchEvent.dispatch(ORGANISATION_STOCK_LIST_QUERY);
      RefetchEvent.dispatch(ORGANISATION_ORDERS_LIST_QUERY);
      RefetchEvent.dispatch(ORGANISATION_ORDER_BALANCE_QUERY);
      RefetchEvent.dispatch(ORGANISATION_COMMISSIONS_LIST_QUERY);
    },
    defaultValues: {
      stockBasket: stockBasket?.id,
      stock: stock.id,
      patient: "",
      organisation: organisation?.id,
      barcode: ""
    },
    transformer: data => ({
      ...data,
      patient: organisation.patients.find(patient => patient.id === patientId)?.patientProfile?.pk
    })
  });

  useDidUpdate(() => {
    if (organisation) {
      formApi.methods.setValue("organisation", organisation.id);
    }
  }, [organisation]);

  const patientId = formApi.methods.watch("patient");
  const patient = organisation.patients.find(patient => patient.id === patientId);

  const [addOnToStockMutation] = useMutation(ADD_ON_TO_STOCK_MUTATION);

  const [removeAddOnFromStockMutation] = useMutation(REMOVE_ADD_ON_FROM_STOCK_MUTATION);

  if (showCreatePatientForm) {
    return (
      <Box px={[20]} maxWidth={760} mx="auto">
        <SavePatientForm
          handleSuccess={({ data }) => {
            const id = data?.savePatientMutation?.patient?.id;
            formApi.methods.setValue("patient", id);
            setShowCreatePatientForm(false);
          }}
          mutationOptions={{
            refetchQueries: [
              { query: ORGANISATION_LIST_QUERY },
              {
                query: DYNAMIC_CMP_PERMISSION_QUERY,
                variables: { organisationId: organisation.id, permission: "can_create_patient" }
              }
            ]
          }}
        />
      </Box>
    );
  }

  if (showEditPatientForm && patient) {
    return (
      <Box px={[20]} maxWidth={760} mx="auto">
        <SavePatientForm
          editableFields={getSavePatientEditableFields(patient)}
          missingFields={getMissingFieldsForPatient(patient)}
          defaultValues={{
            id: patient.id,
            firstName: patient.patientProfile.user.firstName,
            lastName: patient.patientProfile.user.lastName,
            email: patient.patientProfile.user.email,
            sex: patient.patientProfile.gender,
            dateOfBirth: patient.patientProfile.dateOfBirth || "",
            requiresGuardianConsent: patient.requiresGuardianConsent,
            guardianFirstName: patient.patientProfile.guardian?.user.firstName || "",
            guardianLastName: patient.patientProfile.guardian?.user.lastName || "",
            guardianEmail: patient.patientProfile.guardian?.user.email || ""
          }}
          key="edit"
          handleSuccess={({ data }) => {
            setShowEditPatientForm(false);
          }}
        />
      </Box>
    );
  }

  return (
    <>
      <FormProvider {...formApi.methods}>
        <Stack
          gap={[20, 20, 40]}
          as="form"
          onSubmit={formApi.methods.handleSubmit(formApi.onSubmit)}
        >
          <Box>
            <Box fontFamily="gilroyBold" fontSize={[24, 24, 28]} lineHeight="130%">
              Register this kit to a patient
            </Box>
            <Spacer py={2} />
            <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]}>
              Once you register this kit to a patient your business will be invoiced for the
              remaining balance of the test.
            </Box>
          </Box>
          {hasBarcode && (
            <FormControl
              label={
                <Box fontFamily="gilroyBold" fontSize={18} lineHeight="36px">
                  Confirm kit registration number
                </Box>
              }
            >
              <ControlledFormField
                Component={FloatingLabelInput}
                name="barcode"
                label="Enter barcode"
              />
            </FormControl>
          )}
          <ControlledFormField
            Component={SelectPatientField}
            label="Select patient"
            patients={organisation.patients}
            name="patient"
            labelProps={{
              fontFamily: "gilroyBold",
              fontSize: 18,
              lineHeight: "36px",
              mb: 20
            }}
          />
          {!!(permissions && permissions.includes(CMP_PERMISSION_ADMIN)) ? (
            <ButtonV2
              color="green"
              onClick={() => {
                setShowCreatePatientForm(true);
              }}
              rightIcon={<ChevronComponent />}
              maxWidth={310}
              type="button"
              size={["sm", "sm", "md"]}
            >
              create a new patient
            </ButtonV2>
          ) : (
            <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]} color="midGrey">
              Please contact your team manager if you need to add additional patients
            </Box>
          )}
          <Divider color="#1A1A1A" />
          <FadeTransition in={!!patient}>
            {patient && (
              <PatientDetailsForRegisteringStock
                patient={patient}
                permissions={permissions}
                onEdit={() => {
                  setShowEditPatientForm(true);
                }}
              />
            )}
          </FadeTransition>
          {!!stock.testProduct?.addons?.length && (
            <>
              <Stack gap={[40]}>
                <Box>
                  <Box fontFamily="gilroyBold" fontSize={[18, 18, 28]} lineHeight="130%">
                    Additional marker add ons
                  </Box>
                  <Spacer py={2} />
                  <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]}>
                    {isCyrexStockProduct ? (
                      <>
                        This test requires at least 1 add on to proceed. N.B The kit fee has already
                        been paid for as part of the stock order.
                      </>
                    ) : (
                      <>
                        This test has additional markers that can be included in your purchase. If
                        you wish to include these markers in the stock registration please select
                        from below.
                      </>
                    )}
                  </Box>
                </Box>
                <Stack gap={1} bg="white">
                  {stock.testProduct?.addons.map(addon => (
                    <AddOnDetail
                      key={addon.id}
                      name={addon.name}
                      description={addon.content.description}
                      price={addon.tradeCurrentPrice}
                      state={
                        stock.addons.map(addon => addon.id).includes(addon.id)
                          ? "selected"
                          : "unselected"
                      }
                      onRemove={() =>
                        removeAddOnFromStockMutation({
                          variables: {
                            input: {
                              addon: addon.id,
                              stock: stock?.id,
                              organisation: organisation?.id,
                              stockBasketId: stockBasket?.id
                            }
                          }
                        })
                      }
                      onAdd={() =>
                        addOnToStockMutation({
                          variables: {
                            input: {
                              addon: addon.id,
                              stock: stock?.id,
                              organisation: organisation?.id,
                              stockBasketId: stockBasket?.id
                            }
                          }
                        })
                      }
                    />
                  ))}
                </Stack>
              </Stack>
              <Divider color="#1A1A1A" />
            </>
          )}
          <Box>
            <Box fontFamily="gilroyBold" fontSize={[18, 18, 28]}>
              Discount
            </Box>
            <Spacer py={[2, 2, 15]} />
            <Stack>
              <FloatingLabelInput
                editable={!stockBasket?.appliedTradeDiscount}
                label="Enter Discount"
                onChange={e => {
                  setDiscountInputValue(e.target.value);
                }}
                rightIcon={
                  stockBasket?.appliedTradeDiscount ? (
                    <ButtonV2
                      type="button"
                      sx={{
                        color: "red",
                        fontSize: 18,
                        textTransform: "unset",
                        letterSpacing: "unset",
                        fontFamily: "gilroyMedium",
                        p: 0
                      }}
                      onClick={removeDiscountFromStock}
                    >
                      Remove
                    </ButtonV2>
                  ) : (
                    <ButtonV2
                      type="button"
                      sx={{
                        color: "green",
                        fontSize: 18,
                        textTransform: "unset",
                        letterSpacing: "unset",
                        fontFamily: "gilroyMedium",
                        p: 0
                      }}
                      onClick={() => {
                        applyDiscountToStock(discountInputValue);
                      }}
                    >
                      Apply
                    </ButtonV2>
                  )
                }
                value={discountInputValue}
              />
              {/* discountErrors should only be if a discount was NOT applied i.e. we should have no discount on the basket */}
              {/* discountInvalidReason should only be if a discount code is on the basket but is now not valid e.g. they switched to a stock basket */}
              {discountErrors &&
                !stockBasket?.appliedTradeDiscount &&
                discountErrors.map(error => <Error error={error} key={error} />)}
              {!!stockBasket.discountInvalidReason && stockBasket?.appliedTradeDiscount && (
                <Error error={stockBasket.discountInvalidReason} />
              )}
            </Stack>
          </Box>
          <Box fontFamily="gilroyBold" fontSize={[18, 18, 28]} lineHeight="130%">
            Remaining Balance
          </Box>
          <Stack width="100%" alignItems="flex-end" gap={0}>
            <Box>
              <Currency
                data-testid="TestTotal"
                prefix={"TEST TOTAL"}
                value={
                  stockBasket.productLineItems[0].pricePerUnit +
                  stockBasket.productLineItems[0].addons.reduce(
                    (acc, addon) => acc + addon.pricePerUnit,
                    0
                  )
                }
                symbol={stockBasket?.currencySymbol || "£"}
                fontSize={28}
              />
              <Spacer py={1} />
            </Box>
            <Box>
              <Currency
                data-testid="ReturnShipping"
                prefix={"RETURN SHIPPING"}
                value={stockBasket?.basketShippingCost}
                symbol={stockBasket?.currencySymbol || "£"}
                fontSize={28}
              />
              <Spacer py={1} />
            </Box>
            {!!stockBasket?.basketTotalDiscount && (
              <Box>
                <Currency
                  data-testid="DiscountTotal"
                  prefix={"DISCOUNT"}
                  value={stockBasket?.basketTotalDiscount * -1}
                  symbol={stockBasket?.currencySymbol || "£"}
                  fontSize={28}
                  color="red"
                />
                <Spacer py={1} />
              </Box>
            )}
            <Box>
              <Currency
                data-testid="GrandTotal"
                prefix={"TOTAL"}
                value={stockBasket.basketTotalPrice}
                symbol={stockBasket?.currencySymbol || "£"}
                fontSize={44}
              />
              <Spacer py={1} />
            </Box>
          </Stack>
          {formApi.loading ? (
            <Center width="150px" height="57px">
              <Spinner margin="auto" />
            </Center>
          ) : (
            <Box width="100%" flexDirection="row" display="flex" justifyContent="flex-end">
              <FormSubmitButton loading={formApi.loading} requiresBarcode={hasBarcode} />
            </Box>
          )}
          <FadeTransition in={!!formApi.error}>
            <Box color="red" fontSize={16}>
              {formApi.error?.message}
            </Box>
          </FadeTransition>
          <FadeTransition in={!!formApi.nonFieldError}>
            <Center>
              <Error data-testid="nonFieldError" error={formApi.nonFieldError} />
            </Center>
          </FadeTransition>
        </Stack>
      </FormProvider>
    </>
  );
}
